'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import { MapPin } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { Input } from './input'
import { Label } from './label'
import { isBodyScrollLocked } from './popover'

/**
 * Structured US-style address form with optional real-address typeahead.
 *
 * The host supplies `fetchSuggestions`/`fetchDetails` (typically thin wrappers
 * over its own `/api/places/*` proxy routes); picking a suggestion fills every
 * part. The manual line1/line2/city/state/zip inputs are always rendered, so
 * the field degrades gracefully when no autocomplete backend is configured
 * and stays editable after a pick.
 *
 * Positioning: suggestions render in a Radix Popover portaled to <body>
 * (see packages/ui/CLAUDE.md — floating content must be portaled).
 */

export interface AddressInputValue {
  line1: string
  line2?: string
  city: string
  stateProvince: string
  postalCode: string
  /** Full display string from the autocomplete provider, when one was used. */
  formatted?: string
}

export interface AddressSuggestion {
  placeId: string
  /** Display line, e.g. "123 Main St, Springfield, IL, USA". */
  text: string
}

export interface AddressInputProps {
  value: AddressInputValue
  onChange: (next: AddressInputValue) => void
  /** Autocomplete backend. Absent → manual entry only. */
  fetchSuggestions?: (input: string) => Promise<AddressSuggestion[]>
  /** Resolve a picked suggestion into structured parts. */
  fetchDetails?: (placeId: string) => Promise<AddressInputValue | null>
  id?: string
  disabled?: boolean
  className?: string
}

const SUGGEST_DEBOUNCE_MS = 250
const MIN_QUERY_LENGTH = 4

export function AddressInput({
  value,
  onChange,
  fetchSuggestions,
  fetchDetails,
  id,
  disabled = false,
  className,
}: AddressInputProps) {
  const [suggestions, setSuggestions] = React.useState<AddressSuggestion[]>([])
  const [open, setOpen] = React.useState(false)
  const [highlighted, setHighlighted] = React.useState(0)
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeq = React.useRef(0)

  const canSuggest = Boolean(fetchSuggestions)

  function patch(partial: Partial<AddressInputValue>) {
    // Any manual edit invalidates the provider's formatted string.
    const { formatted: _formatted, ...rest } = value
    onChange({ ...rest, ...partial })
  }

  function handleLine1Change(line1: string) {
    patch({ line1 })
    if (!canSuggest || disabled) {
      return
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    const query = line1.trim()
    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      setOpen(false)
      return
    }
    debounceRef.current = setTimeout(() => {
      const seq = ++requestSeq.current
      fetchSuggestions!(query)
        .then((results) => {
          if (seq !== requestSeq.current) {
            return
          }
          setSuggestions(results)
          setHighlighted(0)
          setOpen(results.length > 0)
        })
        .catch(() => {
          if (seq === requestSeq.current) {
            setSuggestions([])
            setOpen(false)
          }
        })
    }, SUGGEST_DEBOUNCE_MS)
  }

  async function pickSuggestion(suggestion: AddressSuggestion) {
    setOpen(false)
    setSuggestions([])
    if (!fetchDetails) {
      // No details resolver — best effort: keep the picked line as line1.
      onChange({ ...value, line1: suggestion.text, formatted: suggestion.text })
      return
    }
    try {
      const details = await fetchDetails(suggestion.placeId)
      if (details) {
        onChange(details)
      }
    } catch {
      // Keep whatever the user typed; manual entry still works.
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) {
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlighted((current) => Math.min(current + 1, suggestions.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted((current) => Math.max(current - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const suggestion = suggestions[highlighted]
      if (suggestion) {
        void pickSuggestion(suggestion)
      }
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  React.useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    },
    [],
  )

  return (
    <div className={cn('space-y-2', className)}>
      <PopoverPrimitive.Root modal={open && isBodyScrollLocked()} open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <Input
            id={id}
            value={value.line1}
            placeholder={canSuggest ? 'Start typing the street address…' : 'Street address'}
            autoComplete="off"
            disabled={disabled}
            aria-label="Street address"
            role={canSuggest ? 'combobox' : undefined}
            aria-expanded={canSuggest ? open : undefined}
            aria-autocomplete={canSuggest ? 'list' : undefined}
            onChange={(event) => handleLine1Change(event.target.value)}
            onKeyDown={handleKeyDown}
          />
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={8}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            style={{ width: 'var(--radix-popper-anchor-width)' }}
            className="z-50 max-h-[min(var(--radix-popover-content-available-height),260px)] max-w-[calc(100vw-16px)] overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
            <ul role="listbox" aria-label="Address suggestions">
              {suggestions.map((suggestion, index) => (
                <li key={suggestion.placeId} role="option" aria-selected={index === highlighted}>
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                      index === highlighted ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                    )}
                    onMouseEnter={() => setHighlighted(index)}
                    // onMouseDown so the pick lands before the input's blur.
                    onMouseDown={(event) => {
                      event.preventDefault()
                      void pickSuggestion(suggestion)
                    }}>
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 break-words">{suggestion.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>

      <Input
        value={value.line2 ?? ''}
        placeholder="Suite, unit, floor (optional)"
        autoComplete="off"
        disabled={disabled}
        aria-label="Address line 2"
        onChange={(event) => patch({ line2: event.target.value || undefined })}
      />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="col-span-2 space-y-1">
          <Label className="text-xs text-muted-foreground">City</Label>
          <Input
            value={value.city}
            autoComplete="off"
            disabled={disabled}
            aria-label="City"
            onChange={(event) => patch({ city: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">State</Label>
          <Input
            value={value.stateProvince}
            autoComplete="off"
            disabled={disabled}
            aria-label="State"
            onChange={(event) => patch({ stateProvince: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">ZIP</Label>
          <Input
            value={value.postalCode}
            autoComplete="off"
            disabled={disabled}
            aria-label="ZIP code"
            onChange={(event) => patch({ postalCode: event.target.value })}
          />
        </div>
      </div>
    </div>
  )
}
